/**
 * Operation Mode — evolution trees (Plague-style DNA upgrades).
 * Three trees: infiltration vectors, dominance methods, stealth abilities.
 * Headless: pure data + effect aggregation, no DOM, no Math.random.
 */

import type { Climate } from './regions.js';

export type EvoTree = 'vector' | 'dominance' | 'stealth';
export type OperationRace = 'grey' | 'lizard' | 'doppel';

export interface EvoNode {
  id: string;
  tree: EvoTree;
  nameKey: string;
  descKey: string;
  cost: number;            // DNA cost
  prereq: string[];        // node ids that must be bought first
  // --- effects (all optional; summed/merged during aggregation) ---
  spreadMul?: number;      // vector: added to global spread multiplier
  landMul?: number;        // vector: added to adjacency bleed multiplier
  airVector?: number;      // vector: adds non-adjacent air seeding (0..1)
  seaVector?: number;      // vector: adds non-adjacent sea seeding (0..1)
  climate?: Partial<Record<Climate, number>>; // vector: per-climate spread bonus
  dnaMul?: number;         // dominance: added to DNA generation multiplier
  dnaFlat?: number;        // dominance: flat DNA per tick
  dominanceMul?: number;   // dominance: deepens control (DNA from infection)
  exposureAdd?: number;    // dominance: raises exposure (aggression cost)
  stealthReduce?: number;  // stealth: flat exposure reduction
  resistanceSlow?: number; // stealth: multiplies resistance growth (<1 slows)
}

/** All evolution nodes, 6 per tree = 18 total. */
export const EVOLUTION_NODES: EvoNode[] = [
  // === Vector: how the infiltration spreads ===
  { id: 'vec_land', tree: 'vector', nameKey: 'op_evo_vec_land', descKey: 'op_evo_vec_land_d', cost: 25, prereq: [], landMul: 0.6 },
  { id: 'vec_air', tree: 'vector', nameKey: 'op_evo_vec_air', descKey: 'op_evo_vec_air_d', cost: 55, prereq: ['vec_land'], airVector: 0.35 },
  { id: 'vec_sea', tree: 'vector', nameKey: 'op_evo_vec_sea', descKey: 'op_evo_vec_sea_d', cost: 55, prereq: ['vec_land'], seaVector: 0.35 },
  { id: 'vec_tropical', tree: 'vector', nameKey: 'op_evo_vec_tropical', descKey: 'op_evo_vec_tropical_d', cost: 80, prereq: ['vec_air'], climate: { tropical: 0.5, arid: 0.3 } },
  { id: 'vec_cold', tree: 'vector', nameKey: 'op_evo_vec_cold', descKey: 'op_evo_vec_cold_d', cost: 80, prereq: ['vec_sea'], climate: { cold: 0.5, temperate: 0.25 } },
  { id: 'vec_surge', tree: 'vector', nameKey: 'op_evo_vec_surge', descKey: 'op_evo_vec_surge_d', cost: 150, prereq: ['vec_tropical', 'vec_cold'], spreadMul: 0.5 },

  // === Dominance: how deeply humans are controlled (more DNA, more exposure) ===
  { id: 'dom_brainwash', tree: 'dominance', nameKey: 'op_evo_dom_brainwash', descKey: 'op_evo_dom_brainwash_d', cost: 30, prereq: [], dnaMul: 0.3, exposureAdd: 0.15 },
  { id: 'dom_memory', tree: 'dominance', nameKey: 'op_evo_dom_memory', descKey: 'op_evo_dom_memory_d', cost: 70, prereq: ['dom_brainwash'], dnaMul: 0.4, exposureAdd: 0.2 },
  { id: 'dom_harvest', tree: 'dominance', nameKey: 'op_evo_dom_harvest', descKey: 'op_evo_dom_harvest_d', cost: 90, prereq: ['dom_memory'], dnaFlat: 3 },
  { id: 'dom_doppel', tree: 'dominance', nameKey: 'op_evo_dom_doppel', descKey: 'op_evo_dom_doppel_d', cost: 110, prereq: ['dom_memory'], dominanceMul: 0.4, dnaFlat: 2, exposureAdd: 0.3 },
  { id: 'dom_reptile', tree: 'dominance', nameKey: 'op_evo_dom_reptile', descKey: 'op_evo_dom_reptile_d', cost: 160, prereq: ['dom_doppel'], dnaMul: 0.6, exposureAdd: 0.4 },
  { id: 'dom_hive', tree: 'dominance', nameKey: 'op_evo_dom_hive', descKey: 'op_evo_dom_hive_d', cost: 200, prereq: ['dom_reptile', 'dom_harvest'], dnaMul: 0.5, dominanceMul: 0.3 },

  // === Stealth: staying hidden (reduce exposure, slow resistance) ===
  { id: 'st_camo', tree: 'stealth', nameKey: 'op_evo_st_camo', descKey: 'op_evo_st_camo_d', cost: 30, prereq: [], stealthReduce: 0.4 },
  { id: 'st_evade', tree: 'stealth', nameKey: 'op_evo_st_evade', descKey: 'op_evo_st_evade_d', cost: 70, prereq: ['st_camo'], resistanceSlow: 0.85 },
  { id: 'st_borders', tree: 'stealth', nameKey: 'op_evo_st_borders', descKey: 'op_evo_st_borders_d', cost: 100, prereq: ['st_camo'], stealthReduce: 0.5 },
  { id: 'st_misinfo', tree: 'stealth', nameKey: 'op_evo_st_misinfo', descKey: 'op_evo_st_misinfo_d', cost: 140, prereq: ['st_evade'], resistanceSlow: 0.8 },
  { id: 'st_deepcover', tree: 'stealth', nameKey: 'op_evo_st_deepcover', descKey: 'op_evo_st_deepcover_d', cost: 120, prereq: ['st_borders'], stealthReduce: 0.7 },
  { id: 'st_ghost', tree: 'stealth', nameKey: 'op_evo_st_ghost', descKey: 'op_evo_st_ghost_d', cost: 210, prereq: ['st_misinfo', 'st_deepcover'], stealthReduce: 0.5, resistanceSlow: 0.75 },
];

const NODE_MAP: Record<string, EvoNode> =
  Object.fromEntries(EVOLUTION_NODES.map((n) => [n.id, n]));

/** Look up an evolution node by id. */
export function getEvoNode(id: string): EvoNode | undefined {
  return NODE_MAP[id];
}

/** Node ids belonging to a tree, in definition order. */
export function nodesForTree(tree: EvoTree): EvoNode[] {
  return EVOLUTION_NODES.filter((n) => n.tree === tree);
}

/** Whether all prerequisites for a node are present in the bought set. */
export function prereqMet(nodeId: string, bought: string[]): boolean {
  const node = NODE_MAP[nodeId];
  if (!node) return false;
  return node.prereq.every((p) => bought.includes(p));
}

/** Aggregated effect bundle read by the spread/resistance/dna systems. */
export interface EvoEffects {
  spreadMul: number;
  landMul: number;
  airVector: number;
  seaVector: number;
  climate: Partial<Record<Climate, number>>;
  dnaMul: number;
  dnaFlat: number;
  dominanceMul: number;
  exposureAdd: number;
  stealthReduce: number;
  resistanceSlow: number; // multiplier applied to resistance growth
}

/** Base effects for a race (starting traits before any evolution). */
export function raceBaseEffects(race: OperationRace): EvoEffects {
  const base: EvoEffects = {
    spreadMul: 1, landMul: 1, airVector: 0, seaVector: 0, climate: {},
    dnaMul: 1, dnaFlat: 0, dominanceMul: 1, exposureAdd: 0,
    stealthReduce: 0, resistanceSlow: 1,
  };
  if (race === 'grey') return { ...base, spreadMul: 1.25 };
  if (race === 'lizard') return { ...base, spreadMul: 0.95, dnaMul: 1.15, dominanceMul: 1.3, exposureAdd: 0.1 };
  return { ...base, spreadMul: 0.9, stealthReduce: 0.6, resistanceSlow: 0.85 }; // doppel
}

/** Fold one node's contributions into an accumulator (mutates + returns it). */
function applyNode(acc: EvoEffects, node: EvoNode): EvoEffects {
  acc.spreadMul += node.spreadMul ?? 0;
  acc.landMul += node.landMul ?? 0;
  acc.airVector += node.airVector ?? 0;
  acc.seaVector += node.seaVector ?? 0;
  acc.dnaMul += node.dnaMul ?? 0;
  acc.dnaFlat += node.dnaFlat ?? 0;
  acc.dominanceMul += node.dominanceMul ?? 0;
  acc.exposureAdd += node.exposureAdd ?? 0;
  acc.stealthReduce += node.stealthReduce ?? 0;
  if (node.resistanceSlow != null) acc.resistanceSlow *= node.resistanceSlow;
  if (node.climate) {
    for (const [k, v] of Object.entries(node.climate)) {
      const key = k as Climate;
      acc.climate[key] = (acc.climate[key] ?? 0) + (v ?? 0);
    }
  }
  return acc;
}

/** Aggregate race base + all bought nodes into a single EvoEffects bundle. */
export function aggregateEvoEffects(boughtNodes: string[], race: OperationRace): EvoEffects {
  const acc = raceBaseEffects(race);
  acc.climate = { ...acc.climate };
  for (const id of boughtNodes) {
    const node = NODE_MAP[id];
    if (node) applyNode(acc, node);
  }
  return acc;
}
